import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';

import { CmsPageSection } from '../../../../models/content';
import { BlockPreviewRequest } from '../../block-preview/block-preview-request';
import { SectionsEditorSource } from '../../block-preview/block-preview-source';
import { ContentSectionsEditorComponent } from '../../content-sections-editor.component';
import { StagePreviewService } from '../workspace/stage-preview.service';

/**
 * One block inserted on a suburb's page, in the CMS block editor (#262): any section but a hero, edited on its own.
 * Done hands back the section as the editor stores it; where it goes on the page is the host's. While it is open the
 * stage previews the block on its page (#263; `previewRequest` is the host's PageTabPreview.block).
 */
@Component({
  selector: 'app-suburb-block-editor',
  template: `
    <div class="block-editor">
      <div class="lbl">
        {{ isNew ? 'New block' : 'Edit block' }}
        <span class="meta-inline">only on {{ pageName || 'this page' }}</span>
      </div>
      <app-content-sections-editor mode="insertion"></app-content-sections-editor>
      <div *ngIf="problem" class="err text-sm mb-2">{{ problem }}</div>
      <div class="flex gap-2 justify-content-end">
        <app-block-preview-toggle *ngIf="previewRequest" class="mr-auto"></app-block-preview-toggle>
        <button
          pButton
          type="button"
          class="p-button-text p-button-sm"
          label="Cancel"
          (click)="cancel.emit()"
        ></button>
        <button
          pButton
          type="button"
          class="p-button-sm block-done"
          icon="pi pi-check"
          label="Done"
          (click)="finish()"
        ></button>
      </div>
    </div>
  `,
  styleUrls: ['./suburb-block-editor.component.scss'],
})
export class SuburbBlockEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() section!: CmsPageSection;
  @Input() isNew = false;
  @Input() pageName = '';
  @Input() previewRequest: ((section: CmsPageSection) => BlockPreviewRequest | null) | null = null;
  @Output() done = new EventEmitter<CmsPageSection>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild(ContentSectionsEditorComponent, { static: true })
  editor!: ContentSectionsEditorComponent;

  problem = '';

  private preview: SectionsEditorSource | null = null;
  private readonly loaded$ = new Subject<void>();

  constructor(private stage: StagePreviewService) {}

  ngOnInit(): void {
    this.load();
    const request = this.previewRequest;
    if (!request) return;
    this.preview = new SectionsEditorSource(
      this.editor,
      (sections) => {
        const [section] = Object.values(sections);
        return section ? request(section) : null;
      },
      undefined,
      this.loaded$
    );
    this.stage.show(this.preview);
  }

  ngOnDestroy(): void {
    if (this.preview) this.stage.release(this.preview);
    this.loaded$.complete();
  }

  ngOnChanges(): void {
    if (this.editor) this.load();
  }

  finish(): void {
    this.problem = this.editor.validate(false) ?? '';
    if (this.problem) return;
    const [section] = Object.values(this.editor.toMap());
    if (section) this.done.emit(section);
  }

  private load(): void {
    if (!this.section) return;
    this.problem = '';
    this.editor.initFrom({ [this.section._meta.id]: this.section });
    // One block: open, not collapsed into the outline it would be on a page.
    this.editor.sections.forEach((s) => (s.collapsed = false));
    this.loaded$.next();
  }
}
